import React, { useCallback, useMemo, useState } from 'react';

type AssigneeOption = {
  id: string;
  username: string;
  fullName?: string | null;
  roleKey?: string | null;
  roleLabel?: string | null;
};

type SimpleDeviceOption = {
  id: string;
  customerId?: string | null;
  locationId?: string | null;
  modelId?: string | null;
  serialNumber?: string | null;
  assetNumber?: string | null;
  modelNumber?: string | null;
  manufacturer?: string | null;
  lifecycleStatus?: string | null;
  customer?: {
    id?: string | null;
    companyName?: string | null;
    company?: string | null;
    email?: string | null;
  } | null;
  model?: {
    id?: string | null;
    modelNumber?: string | null;
    manufacturer?: string | null;
  } | null;
};

type UniqueDeviceDraft = {
  modelId: string;
  customerId: string;
  serialNumber: string;
  assetNumber: string;
  assetNumbersEnabled: boolean;
  lifecycleStatus: string;
  manufacturedAt: string;
  installedAt: string;
  commissionedAt: string;
  warrantyStartAt: string;
  warrantyEndAt: string;
};

type UniqueDeviceCreatePayload = {
  modelId: string;
  customerId: string;
  serialNumber: string;
  assetNumber?: string | null;
  assetNumbersEnabled?: boolean;
  lifecycleStatus?: string;
  manufacturedAt?: string | null;
  installedAt?: string | null;
  commissionedAt?: string | null;
  warrantyStartAt?: string | null;
  warrantyEndAt?: string | null;
};

type Props = {
  showInternalJobModal: boolean;
  styles: any;
  onClose: () => void;
  internalJobForm: any;
  setInternalJobForm: React.Dispatch<React.SetStateAction<any>>;
  isInternalJobDragging: boolean;
  setIsInternalJobDragging: React.Dispatch<React.SetStateAction<boolean>>;
  uploadInternalJobDocuments: (files: File[]) => void;
  handleInternalJobFileInput: (event: React.ChangeEvent<HTMLInputElement>) => void;
  internalJobUploadError: string | null;
  internalJobUploads: Array<{
    fileName: string;
    fileUrl: string;
    fileType: string;
    mimeType?: string | null;
    fileSize: number;
  }>;
  setInternalJobUploads: React.Dispatch<
    React.SetStateAction<
      Array<{
        fileName: string;
        fileUrl: string;
        fileType: string;
        mimeType?: string | null;
        fileSize: number;
      }>
    >
  >;
  deviceModels: Array<{ id: string; modelNumber?: string; modelName?: string; manufacturer?: string }>;
  devices: SimpleDeviceOption[];
  customers: Array<{
    id: string;
    companyName?: string | null;
    company?: string | null;
    email?: string | null;
    isInternal?: boolean;
    assetNumbersEnabled?: boolean;
  }>;
  assigneeOptions: AssigneeOption[];
  taskGroupOptions: Array<{ value: string; label: string }>;
  internalJobSerialAudit: Array<{
    serial: string;
    submittedIdentifier?: string;
    matchType?: 'asset' | 'serial' | 'unknown';
    assetNumber?: string | null;
    serialNumber?: string | null;
    deviceModelLabel?: string;
    customerLabel: string;
    statusLabel: string;
    included: boolean;
    reason: string;
    deviceId?: string | null;
  }>;
  internalJobError: string | null;
  onDismissInternalJobError: () => void;
  handleCheckInternalJobSerials: () => void;
  handleGenerateInternalJob: () => void;
  onCreateUniqueDevice?: (payload: UniqueDeviceCreatePayload) => Promise<SimpleDeviceOption>;
  internalJobSubmitting: boolean;
};

const createEmptyUniqueDeviceDraft = (): UniqueDeviceDraft => ({
  modelId: '',
  customerId: '',
  serialNumber: '',
  assetNumber: '',
  assetNumbersEnabled: false,
  lifecycleStatus: 'DEPLOYED',
  manufacturedAt: '',
  installedAt: '',
  commissionedAt: '',
  warrantyStartAt: '',
  warrantyEndAt: ''
});

const optionalDateValue = (value?: string | null) => {
  const trimmed = String(value || '').trim();
  return trimmed || null;
};

const getCustomerLabel = (customer: { companyName?: string | null; company?: string | null; email?: string | null }) =>
  customer.companyName ?? customer.company ?? customer.email ?? 'Customer';

const isInternalCustomer = (customer?: { companyName?: string | null; company?: string | null; isInternal?: boolean } | null) => {
  if (!customer) return false;
  if (Boolean(customer.isInternal)) return true;
  const label = String(customer.companyName ?? customer.company ?? '').trim().toUpperCase();
  return label === 'INTERNAL';
};

const doesDeviceMatchSimpleSelection = (
  device: SimpleDeviceOption,
  customerId: string,
  modelId: string,
  allowUnassigned: boolean
) => {
  if (!device?.id) return false;
  const deviceCustomerId = String(device.customerId || '').trim();
  if (allowUnassigned) {
    if (deviceCustomerId && deviceCustomerId !== customerId) return false;
  } else if (deviceCustomerId !== customerId) {
    return false;
  }
  if (modelId && String(device.modelId || '') !== modelId) return false;
  return true;
};

const getDeviceModelId = (device?: SimpleDeviceOption | null) =>
  String(device?.modelId || device?.model?.id || '').trim();

const getDeviceCustomerId = (device?: SimpleDeviceOption | null) =>
  String(device?.customerId || device?.customer?.id || '').trim();

const getDeviceModelNumber = (device?: SimpleDeviceOption | null) =>
  String(device?.modelNumber || device?.model?.modelNumber || 'Unknown model').trim();

const getDeviceManufacturer = (device?: SimpleDeviceOption | null) =>
  String(device?.manufacturer || device?.model?.manufacturer || '').trim();

const getDeviceSerial = (device?: SimpleDeviceOption | null) =>
  String(device?.serialNumber || '').trim();

const getDeviceAsset = (device?: SimpleDeviceOption | null) =>
  String(device?.assetNumber || '').trim();

const getDeviceLifecycleLabel = (device?: SimpleDeviceOption | null) => {
  const value = String(device?.lifecycleStatus || '').trim().toUpperCase();
  if (value === 'INVENTORY') return 'Inventory';
  if (value === 'INACTIVE') return 'Inactive';
  if (value === 'RETIRED') return 'Retired';
  if (value === 'DEPLOYED') return 'Deployed';
  return value || 'Active';
};

const getDeviceCustomerLabel = (
  device: SimpleDeviceOption,
  customers: Array<{ id: string; companyName?: string | null; company?: string | null; email?: string | null }>
) => {
  const customerId = getDeviceCustomerId(device);
  const matchedCustomer = customers.find(customer => customer.id === customerId);
  if (matchedCustomer) return getCustomerLabel(matchedCustomer);
  const embedded = device.customer;
  return embedded?.companyName ?? embedded?.company ?? embedded?.email ?? 'No customer';
};

export const TechDashboardInternalJobModal = React.memo(function TechDashboardInternalJobModal({
  showInternalJobModal,
  styles,
  onClose,
  internalJobForm,
  setInternalJobForm,
  isInternalJobDragging,
  setIsInternalJobDragging,
  uploadInternalJobDocuments,
  handleInternalJobFileInput,
  internalJobUploadError,
  internalJobUploads,
  setInternalJobUploads,
  deviceModels,
  devices,
  customers,
  assigneeOptions,
  taskGroupOptions,
  internalJobSerialAudit,
  internalJobError,
  onDismissInternalJobError,
  handleCheckInternalJobSerials,
  handleGenerateInternalJob,
  onCreateUniqueDevice,
  internalJobSubmitting
}: Props) {
  const mode = String(internalJobForm.mode || 'SIMPLE').toUpperCase() === 'COMPLEX' ? 'COMPLEX' : 'SIMPLE';
  const isSimple = mode === 'SIMPLE';
  const [showUniqueDeviceForm, setShowUniqueDeviceForm] = useState(false);
  const [uniqueDeviceDraft, setUniqueDeviceDraft] = useState<UniqueDeviceDraft>(createEmptyUniqueDeviceDraft);
  const [uniqueDeviceSaving, setUniqueDeviceSaving] = useState(false);
  const [uniqueDeviceError, setUniqueDeviceError] = useState('');

  const sortedCustomers = useMemo(() => {
    return [...customers].sort((a, b) => {
      const aInternal = isInternalCustomer(a);
      const bInternal = isInternalCustomer(b);
      if (aInternal !== bInternal) return aInternal ? -1 : 1;
      return getCustomerLabel(a).localeCompare(getCustomerLabel(b));
    });
  }, [customers]);

  const selectedSimpleCustomer = useMemo(
    () => customers.find(customer => customer.id === String(internalJobForm.customerId || '').trim()) || null,
    [customers, internalJobForm.customerId]
  );

  const selectedSimpleDevice = useMemo(() => {
    const selectedId = String(internalJobForm.deviceId || '').trim();
    if (!selectedId) return null;
    return devices.find(device => device.id === selectedId) || null;
  }, [devices, internalJobForm.deviceId]);

  const selectedModelId = String(internalJobForm.modelId || '').trim();
  const selectedCustomerId = String(internalJobForm.customerId || '').trim();

  const doesDeviceMatchCurrentSimpleFilters = useCallback((device: SimpleDeviceOption, options?: { modelId?: string; customerId?: string }) => {
    const modelId = String(options?.modelId ?? selectedModelId).trim();
    const customerId = String(options?.customerId ?? selectedCustomerId).trim();
    if (modelId && getDeviceModelId(device) !== modelId) return false;
    if (!customerId) return true;
    const customer = customers.find(entry => entry.id === customerId) || null;
    const allowUnassigned = isInternalCustomer(customer);
    return doesDeviceMatchSimpleSelection(device, customerId, modelId, allowUnassigned);
  }, [customers, selectedCustomerId, selectedModelId]);

  const filteredDeviceModels = useMemo(() => {
    return [...deviceModels].sort((a, b) => String(a.modelNumber || '').localeCompare(String(b.modelNumber || '')));
  }, [deviceModels]);

  const filteredCustomers = useMemo(() => {
    const modelId = selectedModelId;
    const selectedDeviceCustomerId = getDeviceCustomerId(selectedSimpleDevice);
    return sortedCustomers.filter(customer => {
      if (selectedDeviceCustomerId) return customer.id === selectedDeviceCustomerId;
      if (!modelId) return true;
      if (selectedCustomerId && customer.id === selectedCustomerId) return true;
      const allowUnassigned = isInternalCustomer(customer);
      return devices.some(device => {
        const deviceCustomerId = getDeviceCustomerId(device);
        if (getDeviceModelId(device) !== modelId) return false;
        if (allowUnassigned) return !deviceCustomerId || deviceCustomerId === customer.id;
        return deviceCustomerId === customer.id;
      });
    });
  }, [devices, selectedCustomerId, selectedModelId, selectedSimpleDevice, sortedCustomers]);

  const filteredSimpleDevices = useMemo(() => {
    return devices
      .filter(device => doesDeviceMatchCurrentSimpleFilters(device))
      .sort((a, b) => {
        const assetCompare = getDeviceAsset(a).localeCompare(getDeviceAsset(b));
        if (assetCompare !== 0) return assetCompare;
        return getDeviceSerial(a).localeCompare(getDeviceSerial(b));
      });
  }, [devices, doesDeviceMatchCurrentSimpleFilters]);

  const selectedCustomerUsesAssets = Boolean(
    selectedSimpleCustomer?.assetNumbersEnabled ||
      (selectedCustomerId &&
        devices.some(device => getDeviceCustomerId(device) === selectedCustomerId && Boolean(getDeviceAsset(device))))
  );

  const filteredSimpleDevicesWithAssets = filteredSimpleDevices.filter(device => getDeviceAsset(device));
  const anyVisibleAssetNumbers = filteredSimpleDevicesWithAssets.length > 0;
  const anyCustomerUsesAssets = customers.some(customer => Boolean(customer.assetNumbersEnabled));
  const anyDeviceHasAssets = devices.some(device => Boolean(getDeviceAsset(device)));
  const shouldShowAssetSelector = Boolean(
    selectedCustomerUsesAssets ||
      anyVisibleAssetNumbers ||
      (!selectedCustomerId && (anyCustomerUsesAssets || anyDeviceHasAssets))
  );

  const openUniqueDeviceForm = () => {
    const selectedCustomer = customers.find(customer => customer.id === selectedCustomerId) || null;
    setUniqueDeviceError('');
    setUniqueDeviceDraft({
      ...createEmptyUniqueDeviceDraft(),
      modelId: selectedModelId,
      customerId: selectedCustomerId,
      assetNumbersEnabled: Boolean(
        selectedCustomer?.assetNumbersEnabled ||
          selectedCustomerUsesAssets ||
          shouldShowAssetSelector
      )
    });
    setShowUniqueDeviceForm(true);
  };

  const updateUniqueDeviceDraft = (patch: Partial<UniqueDeviceDraft>) => {
    setUniqueDeviceDraft(prev => ({ ...prev, ...patch }));
  };

  const handleUniqueDeviceCustomerChange = (customerId: string) => {
    const selectedCustomer = customers.find(customer => customer.id === customerId) || null;
    updateUniqueDeviceDraft({
      customerId,
      assetNumbersEnabled: Boolean(selectedCustomer?.assetNumbersEnabled)
    });
  };

  const handleSaveUniqueDevice = async () => {
    if (!onCreateUniqueDevice) return;
    const customerId = uniqueDeviceDraft.customerId.trim();
    const modelId = uniqueDeviceDraft.modelId.trim();
    const serialNumber = uniqueDeviceDraft.serialNumber.trim();
    const assetNumber = uniqueDeviceDraft.assetNumber.trim();
    if (!customerId || !modelId || !serialNumber) {
      setUniqueDeviceError('Select a customer, select a device model, and enter a serial number.');
      return;
    }
    setUniqueDeviceSaving(true);
    setUniqueDeviceError('');
    try {
      const createdDevice = await onCreateUniqueDevice({
        modelId,
        customerId,
        serialNumber,
        assetNumber: assetNumber || null,
        assetNumbersEnabled: uniqueDeviceDraft.assetNumbersEnabled,
        lifecycleStatus: uniqueDeviceDraft.lifecycleStatus || 'DEPLOYED',
        manufacturedAt: optionalDateValue(uniqueDeviceDraft.manufacturedAt),
        installedAt: optionalDateValue(uniqueDeviceDraft.installedAt),
        commissionedAt: optionalDateValue(uniqueDeviceDraft.commissionedAt),
        warrantyStartAt: optionalDateValue(uniqueDeviceDraft.warrantyStartAt),
        warrantyEndAt: optionalDateValue(uniqueDeviceDraft.warrantyEndAt)
      });
      setInternalJobForm((prev: any) => ({
        ...prev,
        mode: 'SIMPLE',
        deviceId: createdDevice.id,
        modelId: getDeviceModelId(createdDevice) || modelId,
        customerId: getDeviceCustomerId(createdDevice) || customerId,
        locationId: createdDevice.locationId || prev.locationId || ''
      }));
      setShowUniqueDeviceForm(false);
      setUniqueDeviceDraft(createEmptyUniqueDeviceDraft());
    } catch (error: any) {
      setUniqueDeviceError(error?.message || 'Failed to create unique device.');
    } finally {
      setUniqueDeviceSaving(false);
    }
  };

  const applySimpleDeviceSelection = (deviceId: string) => {
    const selectedDevice = devices.find(device => device.id === deviceId) || null;
    setInternalJobForm((prev: any) => ({
      ...prev,
      deviceId,
      modelId: selectedDevice ? getDeviceModelId(selectedDevice) || prev.modelId : prev.modelId,
      customerId: selectedDevice ? getDeviceCustomerId(selectedDevice) || prev.customerId : prev.customerId,
      locationId: selectedDevice?.locationId || prev.locationId || ''
    }));
  };

  const tabButtonStyle = (tab: 'SIMPLE' | 'COMPLEX') => {
    const active = tab === mode;
    return active
      ? { ...styles.internalJobTabButton, ...styles.internalJobTabButtonActive }
      : styles.internalJobTabButton;
  };

  if (!showInternalJobModal) return null;

  return (
    <div style={styles.modal}>
      <div
        style={{ ...styles.modalContentWide, maxWidth: '1180px', width: '97%' }}
      >
        <div style={styles.modalHeader}>
          <h2 style={styles.modalTitle}>Create Internal Job</h2>
          <button style={styles.closeButton} onClick={onClose}>
            X
          </button>
        </div>

        <div style={styles.internalJobTabs}>
          <button
            type="button"
            style={tabButtonStyle('SIMPLE')}
            onClick={() =>
              setInternalJobForm((prev: any) => ({
                ...prev,
                mode: 'SIMPLE',
                locationId: '',
                serialPrefix: '',
                serialNumbers: '',
                warrantyStatus: '',
                minAgeDays: '',
                maxAgeDays: '',
                includeInactive: false
              }))
            }
          >
            Single
          </button>
          <button
            type="button"
            style={tabButtonStyle('COMPLEX')}
            onClick={() =>
              setInternalJobForm((prev: any) => ({
                ...prev,
                mode: 'COMPLEX',
                deviceId: ''
              }))
            }
          >
            Multi
          </button>
        </div>

        <div style={styles.modalForm}>
          {isSimple ? (
            <>
              <div
                style={{
                  border: '1px solid #e7dccb',
                  borderRadius: '0.85rem',
                  background: '#fffdf9',
                  padding: '1rem',
                  display: 'grid',
                  gap: '0.9rem'
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '0.75rem',
                    flexWrap: 'wrap'
                  }}
                >
                  <span style={styles.modalHelper}>
                    Select a registered unique device, or add the missing unique device without leaving this job.
                  </span>
                  {onCreateUniqueDevice && (
                    <button
                      type="button"
                      style={{ ...styles.button, ...styles.secondaryButton, fontSize: '0.86rem', padding: '0.45rem 0.85rem' }}
                      onClick={openUniqueDeviceForm}
                    >
                      Add unique device
                    </button>
                  )}
                </div>

                <div style={styles.modalRow}>
                  <div style={styles.modalField}>
                    <label style={styles.modalLabel}>Device model</label>
                    <select
                      style={styles.modalSelect}
                      value={internalJobForm.modelId}
                      onChange={(e) => {
                        const nextModelId = e.target.value;
                        setInternalJobForm((prev: any) => {
                          const next = { ...prev, modelId: nextModelId };
                          if (next.deviceId) {
                            const device = devices.find(entry => entry.id === next.deviceId) || null;
                            if (!device || !doesDeviceMatchCurrentSimpleFilters(device, { customerId: next.customerId, modelId: nextModelId })) {
                              next.deviceId = '';
                              next.locationId = '';
                            }
                          }
                          return next;
                        });
                      }}
                    >
                      <option value="">All device models</option>
                      {filteredDeviceModels.map(model => (
                        <option key={model.id} value={model.id}>
                          {model.modelNumber}
                          {model.manufacturer ? ` - ${model.manufacturer}` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div style={styles.modalField}>
                    <label style={styles.modalLabel}>Customer</label>
                    <select
                      style={styles.modalSelect}
                      value={internalJobForm.customerId}
                      onChange={(e) => {
                        const nextCustomerId = e.target.value;
                        setInternalJobForm((prev: any) => {
                          const next = { ...prev, customerId: nextCustomerId };
                          if (next.deviceId) {
                            const device = devices.find(entry => entry.id === next.deviceId) || null;
                            if (!device || !doesDeviceMatchCurrentSimpleFilters(device, { customerId: nextCustomerId, modelId: next.modelId })) {
                              next.deviceId = '';
                              next.locationId = '';
                            }
                          }
                          return next;
                        });
                      }}
                    >
                      <option value="">All customers</option>
                      {filteredCustomers.map(customer => (
                        <option key={customer.id} value={customer.id}>
                          {getCustomerLabel(customer)}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div
                  style={{
                    display: 'grid',
                    gap: '0.75rem',
                    gridTemplateColumns: shouldShowAssetSelector ? 'repeat(2, minmax(220px, 1fr))' : 'minmax(220px, 1fr)'
                  }}
                >
                  {shouldShowAssetSelector && (
                    <div style={styles.modalField}>
                      <label style={styles.modalLabel}>Asset Number</label>
                      <select
                        style={styles.modalSelect}
                        value={internalJobForm.deviceId}
                        onChange={(e) => applySimpleDeviceSelection(e.target.value)}
                      >
                        <option value="">Select asset number</option>
                        {filteredSimpleDevicesWithAssets.map(device => (
                          <option key={`asset-${device.id}`} value={device.id}>
                            {getDeviceAsset(device)} | Serial {getDeviceSerial(device) || 'Not recorded'} | {getDeviceModelNumber(device)}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  <div style={styles.modalField}>
                    <label style={styles.modalLabel}>Serial Number</label>
                    <select
                      style={styles.modalSelect}
                      value={internalJobForm.deviceId}
                      onChange={(e) => applySimpleDeviceSelection(e.target.value)}
                    >
                      <option value="">Select serial number</option>
                      {filteredSimpleDevices.map(device => (
                        <option key={`serial-${device.id}`} value={device.id}>
                          {getDeviceSerial(device) || 'No serial'}{getDeviceAsset(device) ? ` | Asset ${getDeviceAsset(device)}` : ''} | {getDeviceModelNumber(device)}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {selectedSimpleDevice ? (
                  <div
                    style={{
                      border: '1px solid #d9e5e3',
                      borderRadius: '0.8rem',
                      background: '#f7fbfa',
                      padding: '0.9rem',
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
                      gap: '0.8rem'
                    }}
                  >
                    <div>
                      <div style={{ ...styles.modalLabel, textTransform: 'uppercase', letterSpacing: 0 }}>Selected device</div>
                      <div style={{ fontWeight: 800, color: '#123f43' }}>{getDeviceModelNumber(selectedSimpleDevice)}</div>
                      {getDeviceManufacturer(selectedSimpleDevice) && (
                        <div style={styles.modalHelper}>{getDeviceManufacturer(selectedSimpleDevice)}</div>
                      )}
                    </div>
                    {getDeviceAsset(selectedSimpleDevice) && (
                      <div>
                        <div style={{ ...styles.modalLabel, textTransform: 'uppercase', letterSpacing: 0 }}>Asset</div>
                        <div style={{ fontWeight: 800, color: '#172554' }}>{getDeviceAsset(selectedSimpleDevice)}</div>
                      </div>
                    )}
                    <div>
                      <div style={{ ...styles.modalLabel, textTransform: 'uppercase', letterSpacing: 0 }}>Serial</div>
                      <div style={{ fontWeight: 700, color: '#1f2937' }}>{getDeviceSerial(selectedSimpleDevice) || 'Not recorded'}</div>
                    </div>
                    <div>
                      <div style={{ ...styles.modalLabel, textTransform: 'uppercase', letterSpacing: 0 }}>Customer</div>
                      <div style={{ fontWeight: 700, color: '#1f2937' }}>{getDeviceCustomerLabel(selectedSimpleDevice, customers)}</div>
                    </div>
                    <div>
                      <div style={{ ...styles.modalLabel, textTransform: 'uppercase', letterSpacing: 0 }}>Status</div>
                      <div style={{ fontWeight: 700, color: '#1f2937' }}>{getDeviceLifecycleLabel(selectedSimpleDevice)}</div>
                    </div>
                  </div>
                ) : (
                  <div
                    style={{
                      border: '1px dashed #d9d0c3',
                      borderRadius: '0.8rem',
                      background: '#fbf8f1',
                      padding: '0.85rem',
                      display: 'flex',
                      gap: '0.75rem',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      flexWrap: 'wrap'
                    }}
                  >
                    <span style={styles.modalHelper}>
                      {filteredSimpleDevices.length === 0
                        ? 'No registered unique devices match the selected customer/model. Add the missing device before generating this job.'
                        : 'Select a registered asset or serial number before generating a single-device internal job.'}
                    </span>
                  </div>
                )}

                {showUniqueDeviceForm && (
                  <div
                    style={{
                      border: '1px solid #d7e7e4',
                      borderRadius: '0.85rem',
                      background: '#f7fbfa',
                      padding: '1rem',
                      display: 'grid',
                      gap: '0.9rem'
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: '1rem',
                        flexWrap: 'wrap'
                      }}
                    >
                      <div>
                        <div style={{ fontWeight: 800, color: '#123f43' }}>Add unique device</div>
                        <div style={styles.modalHelper}>Save the missing device, then continue creating this internal job.</div>
                      </div>
                      <button
                        type="button"
                        style={{ ...styles.button, ...styles.secondaryButton, fontSize: '0.86rem', padding: '0.4rem 0.8rem' }}
                        onClick={() => {
                          setShowUniqueDeviceForm(false);
                          setUniqueDeviceError('');
                        }}
                        disabled={uniqueDeviceSaving}
                      >
                        Cancel
                      </button>
                    </div>

                    <div style={styles.modalRow}>
                      <div style={styles.modalField}>
                        <label style={styles.modalLabel}>Device model</label>
                        <select
                          style={styles.modalSelect}
                          value={uniqueDeviceDraft.modelId}
                          onChange={(e) => updateUniqueDeviceDraft({ modelId: e.target.value })}
                        >
                          <option value="">Select device model</option>
                          {filteredDeviceModels.map(model => (
                            <option key={`new-device-model-${model.id}`} value={model.id}>
                              {model.modelNumber}
                              {model.manufacturer ? ` - ${model.manufacturer}` : ''}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div style={styles.modalField}>
                        <label style={styles.modalLabel}>Customer</label>
                        <select
                          style={styles.modalSelect}
                          value={uniqueDeviceDraft.customerId}
                          onChange={(e) => handleUniqueDeviceCustomerChange(e.target.value)}
                        >
                          <option value="">Select customer</option>
                          {sortedCustomers.map(customer => (
                            <option key={`new-device-customer-${customer.id}`} value={customer.id}>
                              {getCustomerLabel(customer)}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <label
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.45rem',
                        color: '#51646b',
                        fontSize: '0.86rem',
                        width: 'fit-content'
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={uniqueDeviceDraft.assetNumbersEnabled}
                        onChange={(e) => updateUniqueDeviceDraft({ assetNumbersEnabled: e.target.checked })}
                      />
                      Customer uses asset numbers
                    </label>

                    <div
                      style={{
                        display: 'grid',
                        gap: '0.75rem',
                        gridTemplateColumns: uniqueDeviceDraft.assetNumbersEnabled
                          ? 'repeat(2, minmax(220px, 1fr))'
                          : 'minmax(220px, 1fr)'
                      }}
                    >
                      <div style={styles.modalField}>
                        <label style={styles.modalLabel}>Serial Number</label>
                        <input
                          type="text"
                          style={styles.modalInput}
                          value={uniqueDeviceDraft.serialNumber}
                          onChange={(e) => updateUniqueDeviceDraft({ serialNumber: e.target.value })}
                        />
                      </div>
                      {uniqueDeviceDraft.assetNumbersEnabled && (
                        <div style={styles.modalField}>
                          <label style={styles.modalLabel}>Asset Number</label>
                          <input
                            type="text"
                            style={styles.modalInput}
                            value={uniqueDeviceDraft.assetNumber}
                            onChange={(e) => updateUniqueDeviceDraft({ assetNumber: e.target.value })}
                          />
                        </div>
                      )}
                    </div>

                    <div style={styles.modalRow}>
                      <div style={styles.modalField}>
                        <label style={styles.modalLabel}>Lifecycle status</label>
                        <select
                          style={styles.modalSelect}
                          value={uniqueDeviceDraft.lifecycleStatus}
                          onChange={(e) => updateUniqueDeviceDraft({ lifecycleStatus: e.target.value })}
                        >
                          <option value="DEPLOYED">Deployed</option>
                          <option value="INVENTORY">Inventory</option>
                          <option value="INACTIVE">Inactive</option>
                          <option value="RETIRED">Retired</option>
                        </select>
                      </div>
                      <div style={styles.modalField}>
                        <label style={styles.modalLabel}>Installed At</label>
                        <input
                          type="date"
                          style={styles.modalInput}
                          value={uniqueDeviceDraft.installedAt}
                          onChange={(e) => updateUniqueDeviceDraft({ installedAt: e.target.value })}
                        />
                      </div>
                      <div style={styles.modalField}>
                        <label style={styles.modalLabel}>Commissioned At</label>
                        <input
                          type="date"
                          style={styles.modalInput}
                          value={uniqueDeviceDraft.commissionedAt}
                          onChange={(e) => updateUniqueDeviceDraft({ commissionedAt: e.target.value })}
                        />
                      </div>
                    </div>

                    <div style={styles.modalRow}>
                      <div style={styles.modalField}>
                        <label style={styles.modalLabel}>Manufactured At</label>
                        <input
                          type="date"
                          style={styles.modalInput}
                          value={uniqueDeviceDraft.manufacturedAt}
                          onChange={(e) => updateUniqueDeviceDraft({ manufacturedAt: e.target.value })}
                        />
                      </div>
                      <div style={styles.modalField}>
                        <label style={styles.modalLabel}>Warranty Start</label>
                        <input
                          type="date"
                          style={styles.modalInput}
                          value={uniqueDeviceDraft.warrantyStartAt}
                          onChange={(e) => updateUniqueDeviceDraft({ warrantyStartAt: e.target.value })}
                        />
                      </div>
                      <div style={styles.modalField}>
                        <label style={styles.modalLabel}>Warranty End</label>
                        <input
                          type="date"
                          style={styles.modalInput}
                          value={uniqueDeviceDraft.warrantyEndAt}
                          onChange={(e) => updateUniqueDeviceDraft({ warrantyEndAt: e.target.value })}
                        />
                      </div>
                    </div>

                    {uniqueDeviceError && (
                      <div style={{ color: '#b91c1c', fontSize: '0.84rem' }}>{uniqueDeviceError}</div>
                    )}

                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
                      <button
                        type="button"
                        style={{ ...styles.button, ...styles.primaryButton }}
                        onClick={handleSaveUniqueDevice}
                        disabled={uniqueDeviceSaving}
                      >
                        {uniqueDeviceSaving ? 'Saving device...' : 'Save and use device'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              <div style={styles.modalRow}>
                <div style={styles.modalField}>
                  <label style={styles.modalLabel}>Device model</label>
                  <select
                    style={styles.modalSelect}
                    value={internalJobForm.modelId}
                    onChange={(e) =>
                      setInternalJobForm((prev: any) => ({ ...prev, modelId: e.target.value }))
                    }
                  >
                    <option value="">All models</option>
                    {deviceModels.map(model => (
                      <option key={model.id} value={model.id}>
                        {model.modelNumber} {model.modelName ? `(${model.modelName})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div style={styles.modalField}>
                <label style={styles.modalLabel}>Exact serials or asset numbers</label>
                <textarea
                  rows={4}
                  style={styles.modalTextarea}
                  value={internalJobForm.serialNumbers}
                  onChange={(e) =>
                    setInternalJobForm((prev: any) => ({ ...prev, serialNumbers: e.target.value }))
                  }
                />
                <span style={styles.modalHelper}>
                  Enter exact serials or asset numbers only, one per line or comma-separated. Hyphenated identifiers are treated as exact values.
                </span>
              </div>

              <div style={styles.modalSection}>
                <div style={styles.modalItemTitle}>Serial range and limits</div>
                <div style={styles.modalRow}>
                  <div style={styles.modalField}>
                    <label style={styles.modalLabel}>From serial</label>
                    <input
                      type="text"
                      style={styles.modalInput}
                      value={internalJobForm.serialRangeFrom || ''}
                      onChange={(e) =>
                        setInternalJobForm((prev: any) => ({ ...prev, serialRangeFrom: e.target.value }))
                      }
                    />
                  </div>
                  <div style={styles.modalField}>
                    <label style={styles.modalLabel}>To serial</label>
                    <input
                      type="text"
                      style={styles.modalInput}
                      value={internalJobForm.serialRangeTo || ''}
                      onChange={(e) =>
                        setInternalJobForm((prev: any) => ({ ...prev, serialRangeTo: e.target.value }))
                      }
                    />
                  </div>
                  <div style={{ ...styles.modalField, maxWidth: '150px' }}>
                    <label style={styles.modalLabel}>Case cap</label>
                    <input
                      type="number"
                      min="1"
                      max="5000"
                      step="1"
                      style={styles.modalInput}
                      value={internalJobForm.caseCap || '100'}
                      onChange={(e) =>
                        setInternalJobForm((prev: any) => ({ ...prev, caseCap: e.target.value }))
                      }
                    />
                  </div>
                </div>
                <span style={styles.modalHelper}>
                  Range matching scans registered devices for the selected model and includes only real serials inside the range.
                </span>
                <div style={styles.modalRow}>
                  <div style={styles.modalField}>
                    <label style={styles.modalLabel}>Manufactured from</label>
                    <input
                      type="date"
                      style={styles.modalInput}
                      value={internalJobForm.manufacturedFrom || ''}
                      onChange={(e) =>
                        setInternalJobForm((prev: any) => ({ ...prev, manufacturedFrom: e.target.value }))
                      }
                    />
                  </div>
                  <div style={styles.modalField}>
                    <label style={styles.modalLabel}>Manufactured to</label>
                    <input
                      type="date"
                      style={styles.modalInput}
                      value={internalJobForm.manufacturedTo || ''}
                      onChange={(e) =>
                        setInternalJobForm((prev: any) => ({ ...prev, manufacturedTo: e.target.value }))
                      }
                    />
                  </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.6rem' }}>
                  <button
                    type="button"
                    style={{ ...styles.button, ...styles.secondaryButton }}
                    onClick={handleCheckInternalJobSerials}
                    disabled={internalJobSubmitting}
                  >
                    Find Devices
                  </button>
                </div>
              </div>

              <div style={styles.modalSection}>
                <div style={styles.modalItemTitle}>
                  Found devices ({internalJobSerialAudit.length})
                </div>
                <div
                  style={{
                    border: '1px solid #dbe3f0',
                    borderRadius: '10px',
                    maxHeight: '420px',
                    overflowY: 'auto',
                    background: '#f8fafc'
                  }}
                >
                  {internalJobSerialAudit.length === 0 ? (
                    <div style={{ padding: '0.75rem 0.9rem', color: '#64748b', fontSize: '0.85rem' }}>
                      Run <strong>Find Devices</strong> to validate registered devices before case generation.
                    </div>
                  ) : (
                    internalJobSerialAudit.map((row, index) => (
                      <div
                        key={`serial-audit-${row.deviceId || row.serial}-${index}`}
                        style={{
                          display: 'grid',
                          gridTemplateColumns: 'minmax(110px, 0.9fr) minmax(220px, 1.6fr) minmax(130px, 1fr) 90px minmax(180px, 1fr)',
                          gap: '0.6rem',
                          alignItems: 'center',
                          padding: '0.55rem 0.75rem',
                          borderBottom: '1px solid #e2e8f0',
                          fontSize: '0.82rem'
                        }}
                      >
                        <div>
                          <div style={{ color: '#64748b', textTransform: 'uppercase', fontSize: '0.7rem', fontWeight: 700 }}>
                            {row.matchType === 'asset' ? 'Asset' : row.matchType === 'serial' ? 'Serial' : 'Submitted'}
                          </div>
                          <div style={{ fontWeight: 700, color: '#0f172a' }}>{row.submittedIdentifier || row.serial}</div>
                        </div>
                        <div>
                          <div style={{ fontWeight: 600, color: '#0f172a' }}>{row.deviceModelLabel || 'Registered device'}</div>
                          <div style={{ color: '#64748b' }}>
                            {row.assetNumber || row.serialNumber
                              ? `${row.assetNumber ? `Asset ${row.assetNumber}` : 'No asset'}${row.serialNumber ? ` | Serial ${row.serialNumber}` : ''}`
                              : 'No registered identifier match'}
                          </div>
                        </div>
                        <div style={{ color: '#0f172a' }}>{row.customerLabel}</div>
                        <div style={{ color: '#334155' }}>{row.statusLabel}</div>
                        <div style={{ color: row.included ? '#166534' : '#b91c1c', fontWeight: 700 }}>
                          <div>{row.included ? 'Ready' : 'Excluded'}</div>
                          <div style={{ color: '#64748b', fontWeight: 500 }}>{row.reason}</div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </>
          )}

          <div style={styles.modalRow}>
            <div style={styles.modalField}>
              <label style={styles.modalLabel}>Task</label>
              <select
                style={styles.modalSelect}
                value={internalJobForm.taskGroup}
                onChange={(e) => setInternalJobForm((prev: any) => ({ ...prev, taskGroup: e.target.value }))}
              >
                {taskGroupOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div style={styles.modalField}>
              <label style={styles.modalLabel}>Priority</label>
              <select
                style={styles.modalSelect}
                value={internalJobForm.priority}
                onChange={(e) => setInternalJobForm((prev: any) => ({ ...prev, priority: e.target.value }))}
              >
                <option value="LOW">Low</option>
                <option value="MEDIUM">Medium</option>
                <option value="HIGH">High</option>
                <option value="CRITICAL">Critical</option>
              </select>
            </div>
            <div style={styles.modalField}>
              <label style={styles.modalLabel}>Assignee (optional)</label>
              <select
                style={styles.modalSelect}
                value={internalJobForm.assigneeUserId}
                onChange={(e) =>
                  setInternalJobForm((prev: any) => ({ ...prev, assigneeUserId: e.target.value }))
                }
              >
                <option value="">Unassigned</option>
                {assigneeOptions.map(option => (
                  <option key={option.id} value={option.id}>
                    {option.fullName || option.username}
                    {option.roleLabel ? ` (${option.roleLabel})` : ''}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div style={styles.modalField}>
            <label style={styles.modalLabel}>Title</label>
            <input
              type="text"
              style={styles.modalInput}
              placeholder={isSimple ? 'Optional - auto-generated if left blank' : 'Required'}
              value={internalJobForm.title}
              onChange={(e) => setInternalJobForm((prev: any) => ({ ...prev, title: e.target.value }))}
            />
          </div>

          <div style={styles.modalField}>
            <label style={styles.modalLabel}>Description</label>
            <textarea
              rows={3}
              style={styles.modalTextarea}
              value={internalJobForm.description}
              onChange={(e) => setInternalJobForm((prev: any) => ({ ...prev, description: e.target.value }))}
            />
          </div>

          <div style={styles.modalRow}>
            <div style={styles.modalField}>
              <label style={styles.modalLabel}>Instruction reference (TN / recall)</label>
              <input
                type="text"
                style={styles.modalInput}
                value={internalJobForm.instructionRef}
                onChange={(e) =>
                  setInternalJobForm((prev: any) => ({ ...prev, instructionRef: e.target.value }))
                }
              />
            </div>
            <div style={styles.modalField}>
              <label style={styles.modalLabel}>Attach documents (PDF/Word/Excel/etc.)</label>
              <label
                style={{
                  ...styles.uploadDropzone,
                  ...(isInternalJobDragging ? styles.uploadDropzoneActive : {})
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  setIsInternalJobDragging(true);
                }}
                onDragLeave={() => setIsInternalJobDragging(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setIsInternalJobDragging(false);
                  const files = Array.from(e.dataTransfer.files || []);
                  if (files.length > 0) {
                    uploadInternalJobDocuments(files);
                  }
                }}
              >
                <input
                  type="file"
                  multiple
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.rtf"
                  style={{ display: 'none' }}
                  onChange={handleInternalJobFileInput}
                />
                Drag & drop files here, or click to upload
              </label>
              {internalJobUploadError && (
                <div style={{ color: '#b91c1c', fontSize: '0.8rem' }}>{internalJobUploadError}</div>
              )}
              {internalJobUploads.length > 0 && (
                <div style={styles.uploadList}>
                  {internalJobUploads.map(item => (
                    <div key={`${item.fileUrl}-${item.fileName}`} style={styles.uploadItem}>
                      <span>{item.fileName}</span>
                      <button
                        type="button"
                        style={styles.uploadRemove}
                        onClick={() =>
                          setInternalJobUploads((prev: any[]) =>
                            prev.filter(doc => !(doc.fileUrl === item.fileUrl && doc.fileName === item.fileName))
                          )
                        }
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div style={styles.modalActions}>
            <button
              type="button"
              style={{ ...styles.button, ...styles.primaryButton }}
              onClick={handleGenerateInternalJob}
              disabled={internalJobSubmitting}
            >
              Create & generate cases
            </button>
          </div>
        </div>
      </div>

      {internalJobError && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 10000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(15, 23, 42, 0.35)'
          }}
        >
          <div
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="internal-job-error-title"
            style={{
              width: 'min(520px, calc(100vw - 2rem))',
              background: '#fffdfa',
              border: '1px solid #f1d4d4',
              borderRadius: '12px',
              boxShadow: '0 24px 60px rgba(15, 23, 42, 0.28)',
              padding: '1.25rem'
            }}
          >
            <div
              id="internal-job-error-title"
              style={{ fontWeight: 800, color: '#7f1d1d', fontSize: '1.05rem', marginBottom: '0.5rem' }}
            >
              Device selection needs attention
            </div>
            <div style={{ color: '#991b1b', lineHeight: 1.45, marginBottom: '1rem' }}>{internalJobError}</div>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button
                type="button"
                style={{ ...styles.button, ...styles.primaryButton }}
                onClick={onDismissInternalJobError}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});
